import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  ThemeProvider, 
  createTheme,
  responsiveFontSizes,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid'; 
import CryptoJS from 'crypto-js';
import { db } from '../firebase/firebase'; 
import { doc, setDoc } from 'firebase/firestore'; 
import jsPDF from 'jspdf'; 
import logo from '../assets/ovguru.png'; 
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import Cookies from 'js-cookie'; // Import js-cookie

let theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 700,
    },
  },
});

theme = responsiveFontSizes(theme);
const userName = Cookies.get('userName'); 

const CertificatePage = () => {
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const location = useLocation();
  const { completionDate, courseName } = location.state || {}; 

  const handleGenerateCertificate = async () => {
    const certificateId = uuidv4(); 

    // Create the data object without the signature first
    const certificateData = {
      userName: Cookies.get('userName'), 
      courseName: courseName, 
      completionDate,
      certificateId,
      idType,
      idNumber,
      timestamp: new Date(),
    };

    // Generate and save the digital signature
    const signature = handleDigitalSignature(certificateData); 

    // Update the certificateData with the generated signature
    certificateData.signature = signature;

    // Save the certificate data to Firestore with the signature
    await setDoc(doc(db, 'testCertificates', `${userName}_${courseName}`), certificateData);

    console.log('Certificate data saved with signature:', certificateData);

    // Generate PDF certificate
    generatePDF(certificateData);
  };

  const capitalizeCourseName = (name) => {
    return name
      .replace(/[^a-zA-Z0-9]+/g, ' ') // Replace special characters with a space
      .trim() // Remove leading and trailing spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (date) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options).replace(/(\d+)(st|nd|rd|th)/, '$1$2'); // Add ordinal suffix
  };

  const generatePDF = async (data) => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });
  
    // background color
    doc.setFillColor(240, 240, 255);
    doc.rect(0, 0, 297, 210, "F");
  
    // border
    doc.setDrawColor(70, 130, 180);
    doc.setLineWidth(5);
    doc.rect(5, 5, 287, 200);
  
    // logo
    doc.addImage(logo, 'PNG', 18, 18, 25, 30); 
  
    doc.setFont("helvetica");
  
    // Title
    doc.setFontSize(36);
    doc.setTextColor(70, 130, 180);
    doc.text("Certificate of Completion", 148.5, 40, { align: "center" });
  
    // Subtitle
    doc.setFontSize(18);
    doc.setTextColor(100);
    doc.text("This is to certify that", 148.5, 60, { align: "center" });
  
    // Name
    doc.setFontSize(32);
    doc.setTextColor(50);
    doc.setFont("helvetica", "bold");
    doc.text(data.userName, 148.5, 80, { align: "center" });
  
    // Course completion text
    doc.setFontSize(18);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text("has successfully completed the course", 148.5, 95, { align: "center" });
  
    // Course name
    doc.setFontSize(24);
    doc.setTextColor(70, 130, 180);
    doc.setFont("helvetica", "bold");
    doc.text(capitalizeCourseName(data.courseName), 148.5, 110, { align: "center" }); // Capitalized course name in PDF
  
    // Date
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(`on ${data.completionDate}`, 148.5, 125, { align: "center" });
  
    // decorative line
    doc.setDrawColor(70, 130, 180);
    doc.setLineWidth(0.5);
    doc.line(73.5, 135, 223.5, 135);
  
    // Certificate details
    doc.setFontSize(12);
    doc.setTextColor(80);
    doc.text(`Certificate ID: ${data.certificateId}`, 148.5, 145, { align: "center" });
    //doc.text(`ID Type: ${data.idType}`, 148.5, 152, { align: "center" });
    //doc.text(`ID Number: ${data.idNumber}`, 148.5, 154, { align: "center" });
    
    // Format and add timestamp
    const formattedDate = formatDate(data.timestamp);
    const timestampY = 180; // Y position for the timestamp
    doc.setFontSize(18);
    doc.setTextColor(50);
    doc.setFont("helvetica", "bold");
    doc.text(formattedDate, 20, timestampY); // Position at bottom left corner

    // line underneath the timestamp
    doc.setDrawColor(70, 130, 180);
    doc.setLineWidth(0.5);
    doc.line(20, timestampY + 3, 100, timestampY + 3); // Line under the timestamp

    doc.setFontSize(15);
    doc.setTextColor(70, 130, 180);
    doc.text("Issued On", 20, timestampY + 10); // Position below the blue line
  
    // Load the signature image if it exists
    // Format courseName: decapitalize, remove special characters (including colons), replace with hyphens
    const formattedCourseName = courseName
      .toLowerCase()
      .replace(/[^a-zA-Z]/g, '-') // Remove all non-alphabet characters (including colons) and replace with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .replace(/^-|-$/g, '') // Remove leading and trailing hyphens
      .trim(); // Remove any leading or trailing spaces

    const signatureUrl = await getDownloadURL(ref(storage, `signatures/${formattedCourseName}/signature.png`)).catch(() => null);
  
    if (signatureUrl) {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = signatureUrl;
      img.onload = () => {
        doc.addImage(img, 'PNG', 215, 165, 25, 15);
        doc.save(`Certificate_${data.courseName}.pdf`); // Save the PDF after the image is added
      };
      img.onerror = () => {
        console.error('Error loading image:', signatureUrl);
        doc.setFontSize(14);
        doc.text("Computer Generated Certificate", 180, 180); 
        //doc.save(`Certificate_${data.courseName}.pdf`); // Save the PDF even if the image fails to load
      };
    } else {
      doc.setFontSize(12);
      doc.text("Computer Generated Certificate", 180, 180); 
      doc.save(`Certificate_${data.courseName}.pdf`); // Save the PDF if no image is found
    }
    
      // Signature lines
    doc.setDrawColor(70, 130, 180);
    doc.setLineWidth(0.5);
    doc.line(197, 180, 257, 180);

    doc.setFontSize(12);
    doc.text("Instructor Signature", 227, 188, null, null, "center");
    // Save the PDF
    //doc.save(`Certificate_${data.courseName}.pdf`);
  };

  const handleDigitalSignature = (data) => {
    const dataString = `${data.userName}_${data.courseName}_${data.completionDate}`;
    const signatureHash = CryptoJS.SHA256(dataString).toString(CryptoJS.enc.Hex);
    return signatureHash; 
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false} disableGutters>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Certificate Preview
          </Typography>
          <Typography variant="h5" gutterBottom align="center">
            This is to certify that
          </Typography>
          <Typography variant="h3" gutterBottom align="center">
            {Cookies.get('userName')}
          </Typography>
          <Typography variant="h5" gutterBottom align="center">
            has successfully completed the course
          </Typography>
          <Typography variant="h3" gutterBottom align="center">
            {capitalizeCourseName(courseName)}
          </Typography>
          <Typography variant="h6" gutterBottom align="center">
            on {completionDate}
          </Typography>

          <Box sx={{ mt: 4 }}>
            <TextField
              select
              label="Select ID Type"
              value={idType}
              onChange={(e) => setIdType(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value="student_id">StudentID</MenuItem>
              <MenuItem value="drivers_license">Driver's License</MenuItem>
              <MenuItem value="custom_id">Custom Personal ID</MenuItem>
            </TextField>
            <TextField
              label="Enter ID Number"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateCertificate}
            >
              Generate Certificate
            </Button>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default CertificatePage;